import { useState } from "react";
import { useNavigate } from "react-router";
import { Loader2, Plus } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { formatApiError } from "@/lib/format-api-error";
import { useCreateTierProgram } from "./use-create-program";

// Ports app/admin/tiers/_components/NewTierProgramDialog.tsx (legacy
// Next.js dialog hosting the createTierProgramAction). Two variants —
// default (header pill) and empty (full button in EmptyState). On success
// the caller is navigated to /admin/tiers/:id (matrix tab default).
//
// Slug auto-derives from name on the fly; user can override before submit.
// `is_default` is exposed here because the legacy supports default-on-create
// (the service transactionally clears any prior default); editor-side a
// dedicated "Make default" toggle is the canonical flip after creation.

function slugify(input: string): string {
  return input
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9-\s]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 64);
}

export function CreateTierProgramDialog({
  variant = "default",
}: {
  variant?: "default" | "empty";
}) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  const [description, setDescription] = useState("");
  const [isDefault, setIsDefault] = useState(false);
  const mutation = useCreateTierProgram();

  function reset() {
    setName("");
    setSlug("");
    setSlugTouched(false);
    setDescription("");
    setIsDefault(false);
  }

  function onOpenChange(next: boolean) {
    if (!next) reset();
    setOpen(next);
  }

  function onNameChange(v: string) {
    setName(v);
    if (!slugTouched) setSlug(slugify(v));
  }

  function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const trimmedName = name.trim();
    const trimmedSlug = slug.trim();
    if (!trimmedName || !trimmedSlug) return;
    mutation.mutate(
      {
        name: trimmedName,
        slug: trimmedSlug,
        description: description.trim() ? description.trim() : null,
        is_default: isDefault,
      },
      {
        onSuccess: (res) => {
          setOpen(false);
          reset();
          toast.success("Tier program created");
          navigate(`/admin/tiers/${res.id}`);
        },
        onError: (err) => {
          // useCreateTierProgram already toasts the raw error.message; this
          // override surfaces the zod-validation message (slug-collision,
          // etc.) when present.
          toast.error(formatApiError(err, "Failed to create tier program"));
        },
      },
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        {variant === "empty" ? (
          <Button>
            <Plus className="mr-2 h-4 w-4" />
            Create your first tier program
          </Button>
        ) : (
          <Button size="sm">
            <Plus className="mr-1.5 h-3.5 w-3.5" />
            New tier program
          </Button>
        )}
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight">
            New tier program
          </DialogTitle>
          <DialogDescription>
            Name your program and pick a slug. You can add tiers, requirements
            and benefits in the editor.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={onSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="tp-name">Name</Label>
            <Input
              id="tp-name"
              value={name}
              onChange={(e) => onNameChange(e.target.value)}
              placeholder="e.g. Partner Program"
              required
              maxLength={80}
              autoFocus
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="tp-slug">Slug</Label>
            <Input
              id="tp-slug"
              value={slug}
              onChange={(e) => {
                setSlugTouched(true);
                setSlug(e.target.value);
              }}
              placeholder="partner-program"
              required
              maxLength={64}
              pattern="^[a-z][a-z0-9-]{1,63}$"
            />
            <p className="text-xs text-muted-foreground">
              Lowercase letters, digits and hyphens. Used in URLs and HubSpot
              property values.
            </p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="tp-description">Description (optional)</Label>
            <Textarea
              id="tp-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="A short note shown above the matrix"
              rows={3}
              maxLength={500}
            />
          </div>

          <div className="flex items-center gap-2">
            <Checkbox
              id="tp-is_default"
              checked={isDefault}
              onCheckedChange={(v) => setIsDefault(v === true)}
            />
            <Label htmlFor="tp-is_default" className="font-normal">
              Set as default program
            </Label>
          </div>
          <p className="-mt-2 text-xs text-muted-foreground">
            The default program drives the legacy partners.tier mirror. Only
            one program can be default at a time — setting this unsets any
            prior default.
          </p>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={mutation.isPending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={mutation.isPending || !name.trim() || !slug.trim()}
            >
              {mutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Create program
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
